// src/components/forms/OffendingAnimalInformation.jsx
import React, { useState } from 'react';
import {
    Box,
    Grid,
    TextField,
    Typography,
    Autocomplete,
    FormControlLabel,
    Checkbox,
    MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    ANIMAL_CODES,
    GROUP_LABELS,
    getOptionLabel,
    getBreedsFor,
} from '../../constants/animalcodes';

const SEX_OPTIONS = ['M', 'F', 'UKWN'];
const OWNED_OPTIONS = ['Yes', 'No', 'UKWN'];
const VACC_INTERVAL_OPTIONS = ['1 yr', '3 yr'];

const GROUP_OPTIONS = Object.keys(GROUP_LABELS).map((g) => ({
    value: g,
    label: GROUP_LABELS[g],
}));

const makeAnimalRow = () => ({
    group: '',          // required before selecting type
    type: null,         // { group, code, description }
    breed: '',          // string
    sex: '',
    owned: '',
    vaccInterval: '',
    // other text fields remain uncontrolled
});

export default function OffendingAnimalInformation() {
    const theme = useTheme();
    const [count, setCount] = useState(1);
    const [animals, setAnimals] = useState([makeAnimalRow()]);

    const handleCountChange = (e) => {
        const v = Math.max(0, parseInt(e.target.value, 10) || 0);
        setCount(v);
        setAnimals((prev) => {
            if (v > prev.length) {
                return [...prev, ...Array.from({ length: v - prev.length }, () => makeAnimalRow())];
            }
            return prev.slice(0, v);
        });
    };

    const updateAnimal = (idx, patch) => {
        setAnimals((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], ...patch };
            return next;
        });
    };

    return (
        <Box mb={4}>
            {/* SC DPH�branded gradient header */}
            <Box
                sx={{
                    mb: 2,
                    p: 1,
                    borderRadius: 1,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                }}
            >
                <Typography variant="h6" color="white">
                    Offending Animal Information
                </Typography>
            </Box>

            {/* Number of Offending Animals */}
            <TextField
                label="Number of Offending Animal(s)"
                type="number"
                value={count}
                onChange={handleCountChange}
                InputProps={{ inputProps: { min: 0 } }}
                fullWidth
                margin="normal"
            />

            {/* Dynamic blocks */}
            {Array.from({ length: count }).map((_, idx) => {
                const row = animals[idx] ?? makeAnimalRow();
                const typeOptions = row.group
                    ? ANIMAL_CODES.filter((o) => o.group === row.group)
                    : [];
                const breedOptions = row.type ? getBreedsFor(row.type.code) : [];

                return (
                    <Box
                        key={idx}
                        mb={3}
                        p={2}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            backgroundColor: idx % 2 === 1 ? theme.palette.action.hover : 'transparent',
                        }}
                    >
                        <Typography variant="subtitle1" gutterBottom>
                            Animal #{idx + 1}
                        </Typography>

                        <Grid container spacing={2}>
                            {/* Group (select first) */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Group"
                                    fullWidth
                                    value={row.group}
                                    onChange={(e) => {
                                        const newGroup = e.target.value;
                                        // When group changes, clear downstream fields
                                        updateAnimal(idx, { group: newGroup, type: null, breed: '' });
                                    }}
                                >
                                    {GROUP_OPTIONS.map((g) => (
                                        <MenuItem key={g.value} value={g.value}>
                                            {g.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Type (depends on Group) */}
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={typeOptions}
                                    getOptionLabel={getOptionLabel}
                                    isOptionEqualToValue={(a, b) => a.code === b.code && a.group === b.group}
                                    value={row.type}
                                    onChange={(_, val) => {
                                        // When type changes, clear breed if switching species
                                        updateAnimal(idx, { type: val, breed: '' });
                                    }}
                                    disabled={!row.group}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Type"
                                            fullWidth
                                            placeholder={row.group ? 'Select type' : 'Select a group first'}
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Breed (uses dynamic list where available; e.g., full list for DOG) */}
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    freeSolo
                                    options={breedOptions}
                                    value={row.breed || null}
                                    onInputChange={(_, val) => updateAnimal(idx, { breed: val })}
                                    onChange={(_, val) => updateAnimal(idx, { breed: val || '' })}
                                    disabled={!row.type}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Breed"
                                            fullWidth
                                            placeholder={
                                                row.type
                                                    ? (breedOptions.length ? 'Select or type a breed' : 'Type breed')
                                                    : 'Select a type first'
                                            }
                                            helperText={
                                                row.type && !breedOptions.length
                                                    ? 'No preset list for this animal type; free text allowed.'
                                                    : ''
                                            }
                                        />
                                    )}
                                />
                            </Grid>

                            {/* Size/Color */}
                            <Grid item xs={12} sm={6}>
                                <TextField label="Size/Color" fullWidth />
                            </Grid>

                            {/* Sex */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Sex"
                                    fullWidth
                                    value={row.sex}
                                    onChange={(e) => updateAnimal(idx, { sex: e.target.value })}
                                >
                                    {SEX_OPTIONS.map((o) => (
                                        <MenuItem key={o} value={o}>
                                            {o}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Owned */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Owned"
                                    fullWidth
                                    value={row.owned}
                                    onChange={(e) => updateAnimal(idx, { owned: e.target.value })}
                                >
                                    {OWNED_OPTIONS.map((o) => (
                                        <MenuItem key={o} value={o}>
                                            {o}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Animal�s Name */}
                            <Grid item xs={12} sm={6}>
                                <TextField label="Animals Name" fullWidth />
                            </Grid>

                            {/* Age */}
                            <Grid item xs={12} sm={6}>
                                <TextField label="Age" type="number" fullWidth />
                            </Grid>

                            {/* Owner�s Name */}
                            <Grid item xs={12}>
                                <Box display="flex" gap={1} alignItems="center">
                                    <TextField label="Owners Name" fullWidth />
                                    <FormControlLabel control={<Checkbox />} label="Same as Victim" />
                                </Box>
                            </Grid>

                            {/* Address */}
                            <Grid item xs={12}>
                                <TextField
                                    label="Address (Give Directions, if Not a Street Address)"
                                    fullWidth
                                />
                            </Grid>

                            {/* City / State / Zip */}
                            <Grid item xs={12} sm={4}>
                                <TextField label="City" fullWidth />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField label="State" defaultValue="SC" fullWidth />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField label="Zip" fullWidth />
                            </Grid>

                            {/* Phone / Alternate Phone / Email */}
                            <Grid item xs={12} sm={4}>
                                <TextField label="Phone" fullWidth placeholder="(123) 456-7890" />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField label="Alternate Phone" fullWidth placeholder="(123) 456-7890" />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField label="Email" type="email" fullWidth />
                            </Grid>

                            {/* Current Location & Health Status */}
                            <Grid item xs={12}>
                                <TextField
                                    label="Current Location and Health Status of Animal"
                                    fullWidth
                                    multiline
                                    rows={3}
                                />
                            </Grid>

                            {/* Date of Vaccination */}
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="Date of Vaccination"
                                    type="date"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            {/* Vaccination Interval & Tag # */}
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    select
                                    label="Vaccination Interval"
                                    fullWidth
                                    value={row.vaccInterval}
                                    onChange={(e) => updateAnimal(idx, { vaccInterval: e.target.value })}
                                >
                                    {VACC_INTERVAL_OPTIONS.map((o) => (
                                        <MenuItem key={o} value={o}>
                                            {o}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label="Tag #" fullWidth />
                            </Grid>

                            {/* Veterinarian Clinic / Name */}
                            <Grid item xs={12}>
                                <TextField label="Veterinarian Clinic / Name" fullWidth />
                            </Grid>
                        </Grid>
                    </Box>
                );
            })}
        </Box>
    );
}
